import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import './clientRequests.css';
import { availableBalance } from '../walletFigures';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Client "Cancel Booking" request modal. Accepts either an already-loaded
// booking detail (details page) or just a bookingId (self-fetches). The
// 24h window is judged when the client SUBMITS:
//   pending booking          → removed, nothing charged
//   confirmed, >24h to start → removed + charged amount refunded to the wallet
//   confirmed, <24h to start → kept as Last Minute Cancelled (fee applies,
//                              rest refunded)
export default function RequestBookingCancelModal({ booking: bookingProp, bookingId, onClose, onSubmitted }) {
    const [fetched, setFetched] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    // Wallet snapshot so the client sees exactly what their balance becomes
    // if the cancellation is approved — not just the refund/fee amount.
    const [wallet, setWallet] = useState(null);
    // Block submitting until the wallet snapshot (and thus the refund / balance-
    // after numbers) has finished loading, so the client always confirms against
    // a computed impact — not a half-loaded modal.
    const [walletLoading, setWalletLoading] = useState(true);

    const booking = bookingProp || fetched;

    useEffect(() => {
        if (bookingProp || !bookingId) return;
        (async () => {
            try {
                const res = await axios.get(`/api/client-requests/bookings/${bookingId}`);
                setFetched(res.data);
            } catch (err) {
                console.error('Error loading booking for cancellation:', err);
                setLoadError('Could not load this booking. Please try again.');
            }
        })();
    }, [bookingProp, bookingId]);

    useEffect(() => {
        let stale = false;
        axios.get('/api/client-requests/wallet')
            .then((res) => { if (!stale) setWallet(res.data); })
            .catch((err) => console.error('Error loading wallet for cancellation preview:', err))
            .finally(() => { if (!stale) setWalletLoading(false); });
        return () => { stale = true; };
    }, []);

    if (!booking) {
        return (
            <div className="creq-modal-overlay" onClick={onClose}>
                <div className="creq-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="creq-modal-body" style={{ padding: 24 }}>
                        {loadError
                            ? <div className="creq-error">{loadError}</div>
                            : (
                                <div className="creq-price-panel creq-price-loading">
                                    <span className="creq-price-spinner" aria-hidden="true" />
                                    Loading booking…
                                </div>
                            )}
                    </div>
                </div>
            </div>
        );
    }

    const status = String(booking.booking_status || '').trim().toLowerCase();
    const isPendingBooking = status === 'pending';
    const isLastMinute = !isPendingBooking && booking.lm_window === true;

    // What was ACTUALLY charged from the wallet — this is what refunds are based
    // on (not the booking total). For LM cancels, the fee nets against it.
    const netCharged = Number(booking.net_charged) || 0;
    const lmFee = Number(booking.lm_fee_incl_vat) || 0;
    const lmRefund = Math.round((netCharged - lmFee) * 100) / 100;

    // Signed wallet movement if approved: + = refund lands, − = fee charged.
    const walletDelta = isLastMinute ? lmRefund : netCharged;
    // The fee charge is all-or-nothing: if the wallet can't cover it, nothing
    // is taken and the difference stays as an unpaid amount on the cancelled
    // booking — the balance never goes negative.
    //
    // DELIBERATELY total_balance (G8): this is a PREDICATE about whether the
    // server's debit can physically succeed, and chargeWallet debits the real
    // ledger — which is `total`, not the reservation-netted `available`. Using
    // available here would predict a failure the server won't have. It renders
    // no figure; the headline row below is availableBalance().
    const chargeFails = wallet && walletDelta < 0
        && Number(wallet.total_balance ?? 0) < Math.abs(walletDelta) - 0.005;
    const balanceAfterCancel = wallet
        ? Math.round((availableBalance(wallet) + (chargeFails ? 0 : walletDelta)) * 100) / 100
        : null;

    const when = DateTime.fromISO(booking.booking_start, { zone: 'utc' }).setZone('Asia/Dubai');
    const whenLabel = when.isValid ? when.toFormat('EEE d MMM yyyy, HH:mm') : '—';

    const submit = async () => {
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`/api/client-requests/requests/bookings/${booking.booking_id}/cancel-request`, {
                reason: reason.trim() || undefined,
            });
            setDone(true);
            if (onSubmitted) onSubmitted();
        } catch (err) {
            console.error('Error submitting cancellation request:', err);
            setError(err.response?.data?.error || 'Failed to submit the cancellation request.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="creq-modal-overlay" onClick={onClose}>
            <div className="creq-modal" onClick={(e) => e.stopPropagation()}>
                {done ? (
                    <div className="creq-success">
                        <div className="creq-pending-badge">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                        </div>
                        <span className="creq-pending-chip">Pending review</span>
                        <h3>Cancellation requested</h3>
                        <p>
                            Your booking stays in place until our team approves the cancellation —
                            we&apos;ll notify you the moment it&apos;s processed.
                        </p>
                        <button className="creq-btn creq-btn--primary" onClick={onClose}>Done</button>
                    </div>
                ) : (
                    <>
                        <div className="creq-modal-header">
                            <div>
                                <h3>Cancel Booking</h3>
                                <div className="creq-modal-sub">
                                    Booking #{booking.booking_id} · Unit {booking.unit_number} · {booking.building_name} · {whenLabel}
                                </div>
                            </div>
                            <button className="creq-modal-close" onClick={onClose} title="Close">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="creq-modal-body">
                            {isPendingBooking ? (
                                <div className="creq-cancel-note creq-cancel-note--ok">
                                    {netCharged > 0
                                        ? <>Once approved, this booking is removed and the <strong>{money(netCharged)}</strong> you&apos;ve paid is refunded to your wallet.</>
                                        : <>This booking is still pending, so <strong>nothing has been charged</strong> — once approved, it will simply be removed.</>}
                                </div>
                            ) : isLastMinute ? (
                                <div className="creq-cancel-note creq-cancel-note--warn">
                                    Your visit starts in <strong>less than 24 hours</strong>, so a last-minute
                                    cancellation fee of <strong>{money(lmFee)}</strong> applies.{' '}
                                    {lmRefund > 0.01
                                        ? <><strong>{money(lmRefund)}</strong> of what you paid is refunded to your wallet.</>
                                        : lmRefund < -0.01
                                            ? <><strong>{money(Math.abs(lmRefund))}</strong> will be charged from your wallet to cover the fee.</>
                                            : 'What you paid covers the fee exactly.'}
                                </div>
                            ) : (
                                <div className="creq-cancel-note creq-cancel-note--ok">
                                    {netCharged > 0
                                        ? <>Once approved, this booking is removed and the <strong>{money(netCharged)}</strong> charged is refunded to your wallet.</>
                                        : <>Once approved, this booking is removed — nothing was charged from your wallet, so there is nothing to refund.</>}
                                </div>
                            )}

                            {wallet && (
                                <div className="creq-price-panel">
                                    <div className="creq-price-title">Your wallet</div>
                                    <div className="creq-wallet-rows">
                                        {/* AVAILABLE leads (2026-07-18) — it is the
                                            figure balanceAfterCancel is computed from, so
                                            leading with total made the two rows contradict
                                            each other. Total + what's set aside are the
                                            secondary detail underneath. */}
                                        <div className="creq-price-row">
                                            <span>Available balance</span>
                                            <span>{money(availableBalance(wallet))}</span>
                                        </div>
                                        {Number(wallet.reserved_for_pending) > 0 && (
                                            <div className="creq-price-row creq-wallet-reserved">
                                                <span>
                                                    Wallet total {money(wallet.total_balance)} · set aside for {wallet.pending_bookings_count} other pending request{Number(wallet.pending_bookings_count) === 1 ? '' : 's'}
                                                </span>
                                                <span>−{money(wallet.reserved_for_pending)}</span>
                                            </div>
                                        )}
                                        {Math.abs(walletDelta) > 0.005 ? (
                                            <div className="creq-price-row">
                                                <span>
                                                    {walletDelta > 0 ? 'Refunded if approved'
                                                        : chargeFails ? 'Last-minute fee difference (not covered)'
                                                            : 'Charged if approved (last-minute fee)'}
                                                </span>
                                                <span>{walletDelta > 0 ? `+${money(walletDelta)}` : `−${money(Math.abs(walletDelta))}`}</span>
                                            </div>
                                        ) : (
                                            <div className="creq-price-row">
                                                <span>Wallet change if approved</span>
                                                <span>None</span>
                                            </div>
                                        )}
                                        {balanceAfterCancel != null && (
                                            <div className={`creq-price-row creq-wallet-after ${balanceAfterCancel < 0 ? 'is-negative' : 'is-positive'}`}>
                                                <span>Balance after cancellation (if approved)</span>
                                                <span>{money(balanceAfterCancel)}</span>
                                            </div>
                                        )}
                                        {chargeFails && (
                                            <div className="creq-wallet-warning">
                                                Your wallet doesn&apos;t cover the {money(Math.abs(walletDelta))} fee difference —
                                                nothing is taken from your balance now, and that amount stays as an unpaid
                                                charge on the cancelled booking.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="creq-section-label">Reason (optional)</div>
                            <label className="creq-label creq-label--wide">
                                <textarea
                                    maxLength={500}
                                    placeholder="Let us know why you’re cancelling…"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                />
                            </label>

                            {!!error && <div className="creq-error">{error}</div>}
                        </div>

                        <div className="creq-modal-actions">
                            <button className="creq-btn creq-btn--ghost" onClick={onClose} disabled={submitting}>
                                Keep Booking
                            </button>
                            <button className="creq-btn creq-btn--danger" onClick={submit} disabled={submitting || walletLoading}>
                                {submitting ? 'Submitting…' : walletLoading ? 'Calculating…' : 'Cancel Booking'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
